import { ReactNode, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Header } from '@/components/Header';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { useChrome } from '../context/ChromeContext';
import { ArcheSidebar } from './ArcheSidebar';
import { ExpandedArcheHeader } from './ExpandedArcheHeader';
import { SidebarProvider, useSidebar } from '../context/SidebarContext';
import { SearchResultsProvider } from '../context/SearchResultsContext';

/**
 * Display mode for the ArcheLayout:
 * - 'normal': Full header + sidebar + content (for authenticated users on non-fullscreen arches)
 * - 'public': Minimal header + content, no sidebar (for unauthenticated users on public arches)
 * - 'fullscreen': Minimal header (logo + theme toggle) + content, no sidebar (for full-screen arches)
 * - 'embed': No header at all, just content (for iframe embedding on external sites)
 */
export type ArcheDisplayMode = 'normal' | 'public' | 'fullscreen' | 'embed';

interface ArcheLayoutProps {
  children: ReactNode;
  /**
   * The display mode for the layout.
   * - 'normal': Full header + sidebar + content
   * - 'public': Minimal header + content (no sidebar)
   * - 'fullscreen': Minimal header (logo + theme toggle) + content (no sidebar)
   * - 'embed': No header at all, just content (for iframe embedding)
   */
  displayMode?: ArcheDisplayMode;
  /**
   * The shell theme the arche declared (`fias-plugin.json` `colorScheme`).
   * Undefined and 'platform' both mean "follow the user's Fias theme".
   */
  colorScheme?: 'dark' | 'light' | 'platform';
  /**
   * Suppress the shared focus-mode "expanded" header (Back-to-Fias + Fias AI +
   * Collapse). An arche opts into this when it renders its own restore affordance
   * in a spot that fits its chrome (e.g. the email arche places it above its
   * account selector at the top-left via `FocusModeRestoreControls`). Without an
   * in-arche replacement, leave this false or the arche becomes un-restorable in
   * focus mode.
   */
  hideExpandedHeader?: boolean;
}

/** The dark theme's own background — matches index.html's dark `theme-color`. */
const DARK_SHELL_COLOR = '#0a0a0a';
/** …and the light one, for an arche that asks for light on a dark platform. */
const LIGHT_SHELL_COLOR = '#ffffff';

/**
 * AN ARCHE RUNS ON THE SHELL IT ASKED FOR.
 *
 * `fias-plugin.json`'s `colorScheme` — 'dark' | 'light' | 'platform', default
 * 'platform'. 'platform' means follow the user's own Fias theme, which is what
 * every arche did before this existed, so an arche that says nothing is
 * untouched.
 *
 * The case that wanted it (Tom, 2026-09-10): Flappyland on an iPhone
 * home-screen launch showed a white band across the top. iOS takes the
 * standalone status-bar colour from `theme-color`, ours follows the app theme,
 * and a light-themed platform therefore paints white above a dark game. Worse,
 * an installed window has no address bar and a full-screen arche draws no
 * platform chrome — so the player cannot reach a theme toggle from the screen
 * that needs it.
 *
 * Two things move together, because they answer different surfaces:
 *  - the `dark` class, which the platform's own chrome reads (the loading
 *    screen, anything drawn before the arche paints);
 *  - the LIGHT `theme-color` meta, which is what iOS reads. Pointing it at the
 *    same value as the dark one makes the answer theme-independent while this
 *    is mounted.
 *
 * Deliberately NOT `setTheme()`: next-themes persists to `fias-theme`, so that
 * would silently rewrite the user's saved preference for the whole platform
 * because they opened one arche. This is an override with a lifetime — both
 * halves are restored exactly as they were on unmount.
 *
 * The arche wins over an explicit user choice while it is on screen, and only
 * while it is on screen. The arche is the thing being looked at; the setting
 * survives it.
 */
function useArcheColorScheme(scheme: 'dark' | 'light' | 'platform' | undefined): void {
  useEffect(() => {
    if (scheme !== 'dark' && scheme !== 'light') return;
    const root = document.documentElement;
    const hadDark = root.classList.contains('dark');
    const lightMeta = document.querySelector<HTMLMetaElement>(
      'meta[name="theme-color"][media*="light"]',
    );
    const darkMeta = document.querySelector<HTMLMetaElement>(
      'meta[name="theme-color"][media*="dark"]',
    );
    const priorLight = lightMeta?.content ?? null;
    const priorDark = darkMeta?.content ?? null;
    const wanted = scheme === 'dark' ? DARK_SHELL_COLOR : LIGHT_SHELL_COLOR;

    root.classList.toggle('dark', scheme === 'dark');
    // BOTH metas, so the answer no longer depends on the device's appearance —
    // which is the whole point: the arche asked for one specific shell.
    if (lightMeta) lightMeta.content = wanted;
    if (darkMeta) darkMeta.content = wanted;

    return () => {
      root.classList.toggle('dark', hadDark);
      if (lightMeta && priorLight !== null) lightMeta.content = priorLight;
      if (darkMeta && priorDark !== null) darkMeta.content = priorDark;
    };
  }, [scheme]);
}

function ArcheLayoutInner({
  children,
  displayMode = 'normal',
  colorScheme,
  hideExpandedHeader = false,
}: ArcheLayoutProps) {
  const { id: archeId } = useParams<{ id: string }>();
  const { isCollapsed, isMobile, toggle, setCollapsed } = useSidebar();
  const { isHeadersHidden } = useChrome();
  // Before any early return: the branches below are RETURNS, not conditions,
  // and a hook after one of them would break the rules of hooks.
  useArcheColorScheme(colorScheme);

  // ONE TREE FOR EVERY MODE. `{children}` always sits at the same position —
  // outer div > body div > <main> — and each mode only changes classes and
  // which chrome slots are filled. React keeps a subtree only while its
  // element types and positions are unchanged, so a per-mode tree remounted
  // the arche whenever one arche opened another with a different display
  // mode: a plugin's <iframe> was recreated, and Chrome/Edge start a
  // recreated frame's audio suspended until the user clicks inside it (see
  // the <iframe> note in PluginRenderer). The modes that need no wrapper use
  // `contents` so the wrapper draws no box, and <main> is the scroller in
  // every mode.
  //
  // Embed mode: no header at all, just content (for iframe embedding).
  // FULL-BLEED on purpose — see the fullscreen note below; an embed's edges
  // belong to whatever page is hosting the iframe, not to us.
  //
  // Full-screen mode: no header, no sidebar — content fills the entire viewport.
  //
  // The ONE shell with no safe-area padding, deliberately. The page is
  // `viewport-fit=cover`, so this reaches the physical edges of a notched
  // screen — which is the point: a full-screen arche is the whole display and
  // owns the strip beside the notch, both to paint it and to be touched there.
  // Padding it would put the platform's background back in that strip, which
  // is the white landscape bars this fixed (Tom, 2026-08-04, Flappyland).
  // An arche that wants to keep its own UI clear of the notch reads the insets
  // itself; the SDK's theme carries them.
  //
  // `h-dvh`, NOT `h-screen` (Tom, 2026-09-08: "on mobile, when clicking on a
  // popup that has text, the whole screen shifts in the browser"). On a phone
  // `100vh` is the LARGE viewport — the height the page would have if the
  // address bar were already collapsed — so a shell sized by it is taller than
  // what the player can actually see, and the difference is live scrollable
  // overflow sitting under an arche that believes it owns the window. Any
  // touch that the arche's own document does not consume slides the whole page
  // by that strip and the address bar animates in and out with it. `100dvh`
  // tracks the viewport as it actually is, so the shell ends exactly where the
  // screen does and there is nothing left to scroll. `overscroll-none` closes
  // the other half: a gesture that reaches the end of a scroller inside the
  // arche stops there instead of chaining out to the document and rubber-
  // banding the page.
  //
  // `overflow-auto` STAYS: a full-screen arche is not always a game that fits
  // the window (the beta-access admin page is a form that runs past it), and
  // clipping it would make the bottom of such a page unreachable. What changes
  // is that the scroller is now exactly viewport-tall, so an arche that does
  // fill it has no overflow to produce.
  //
  // Public mode (not logged in): minimal header, no sidebar.
  //
  // Normal mode: full header + sidebar — unless this arche is in focus mode,
  // in which case the platform Header AND the suggested-arches sidebar are
  // dropped so the arche fills the viewport, and a single thin header replaces
  // them with the way back (leave to Fias, open Fias AI, collapse). State is
  // per-arche (default visible) so other arches are untouched.
  const isNormal = displayMode === 'normal';
  const hasChrome = isNormal || displayMode === 'public';
  const headersHidden = isNormal && isHeadersHidden(archeId);
  const showSidebar = isNormal && !headersHidden;

  const outerClass = hasChrome ? 'flex flex-col h-screen pt-safe pb-safe px-safe' : 'contents';
  const bodyClass = isNormal ? 'flex flex-1 overflow-hidden' : 'contents';
  const mainClass =
    displayMode === 'embed'
      ? 'h-screen overflow-auto'
      : displayMode === 'fullscreen'
        ? 'h-dvh overflow-auto overscroll-none'
        : 'flex-1 overflow-auto';

  return (
    <div className={outerClass}>
      {hasChrome && !headersHidden && <Header minimal={displayMode === 'public'} />}
      {/* Expanded (focus mode): the platform Header, the sidebar, and the
          arche's own ArcheHeader are all hidden, so this thin bar is the single,
          consistent way back. Arches with their own layout opt out via
          `hideExpandedHeader` and render their own restore affordance. */}
      {headersHidden && !hideExpandedHeader && archeId && <ExpandedArcheHeader archeId={archeId} />}
      <div className={bodyClass}>
        {/* Desktop Sidebar - only render on desktop and when chrome is shown */}
        {showSidebar && !isMobile && (
          <aside
            className={`
              flex-shrink-0 border-r border-border bg-card overflow-hidden
              transition-all duration-300 ease-in-out
              ${isCollapsed ? 'w-0' : 'w-64'}
            `}
          >
            <div className="w-64 h-full flex flex-col">
              <ArcheSidebar currentArcheId={archeId || ''} onToggle={toggle} />
            </div>
          </aside>
        )}

        {/* Mobile Sheet Sidebar - only on mobile and when chrome is shown (in
            focus mode the toggle that opens it is gone, so don't leave it). */}
        {showSidebar && isMobile && (
          <Sheet open={!isCollapsed} onOpenChange={(open) => setCollapsed(!open)}>
            <SheetContent side="left" className="w-[280px] p-0 [&>button]:hidden">
              <div className="h-full flex flex-col">
                <ArcheSidebar currentArcheId={archeId || ''} onToggle={toggle} />
              </div>
            </SheetContent>
          </Sheet>
        )}

        {/* Main content */}
        <main className={mainClass}>{children}</main>
      </div>
    </div>
  );
}

export function ArcheLayout({
  children,
  displayMode = 'normal',
  colorScheme,
  hideExpandedHeader = false,
}: ArcheLayoutProps) {
  return (
    <SidebarProvider>
      <SearchResultsProvider>
        <ArcheLayoutInner
          displayMode={displayMode}
          colorScheme={colorScheme}
          hideExpandedHeader={hideExpandedHeader}
        >
          {children}
        </ArcheLayoutInner>
      </SearchResultsProvider>
    </SidebarProvider>
  );
}

export default ArcheLayout;
